import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { LOGIN_MUTATION } from '../graphql/mutations/auth';
import type { LoginResponse, LoginVariables } from '../types/auth';
import { useAuth } from '../lib/auth';
import { getApiEndpoint } from '../utils/apiUrl';
import googleLogo from '../data/google_logo.svg.png';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Redirection automatique si déjà connecté
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const [loginMutation, { loading }] = useMutation<LoginResponse, LoginVariables>(
    LOGIN_MUTATION,
    {
      onCompleted: (data) => {
        
        // Stocker le token et les informations utilisateur
        localStorage.setItem('token', data.login.token);
        localStorage.setItem('user', JSON.stringify({
          ...data.login.user,
          agencyId: data.login.user.agency?.id ?? null,
          agency: data.login.user.agency ? {
            id: data.login.user.agency.id,
            name: data.login.user.agency.name,
            logo: data.login.user.agency.logo
          } : null
        }));
        
        // Rediriger vers le tableau de bord
        navigate('/admin/dashboard');
      },
      onError: (error) => {
        setError(error.message);
      }
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      await loginMutation({
        variables: {
          email,
          password
        }
      });
    } catch (err) {
      // L'erreur est déjà gérée par onError
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <LogIn className="w-12 h-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Espace Agent de Voyage
        </h2>
        {/* Commenté temporairement
        <div className="mt-2 text-center text-sm text-gray-600">
          <p>Comptes de démonstration :</p>
          <p>African Travel Service : ats@example.com / password</p>
          <p>1001 Voyages : 1001@example.com / password</p>
        </div>
        */}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              {/* 
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Se souvenir de moi
                </label> 
              </div>*/}

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </div>
            {/* Connexion Google masquée
            <div className="flex justify-center items-center"><hr className="w-full border-t border-gray-500"/> <span className="px-2 text-gray-500">Ou</span> <hr className="w-full border-t border-gray-500" /> </div>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  window.location.href = getApiEndpoint('/auth/google');
                }}
                className=" flex justify-center items-center gap-3 py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-black bg-transparent hover:bg-gray-100
                ring-2  ring-blue-500 disabled:opacity-50"
              >
                <img src={googleLogo}  
                alt="Google logo"  width={30} height={30}
                />
               <span> Connexion avec Google</span>
              </button>
            </div>
            */}
          </form>

          {/* Bloc inscription supprimé (création d'agence réservée au super admin) */}
        </div>
      </div>
    </div>
  );
}